/**
 * Oturum durumu — TEK KAYNAK: sunucunun `/auth/me` cevabı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * GİRİŞ CEVABINDAKİ DEĞERLER YEREL DURUMA YAZILMAZ.
 *
 * `POST /auth/login` de `{userId, firmId, isOperator}` döndürüyor. Onu
 * saklamak cazip ama yanlış: iki kaynak zamanla ayrışır. Operatör firma
 * değiştirir, oturum sunucuda düşer, izinler güncellenir — yerel kopya
 * bunların hiçbirini bilmez ve ekran gerçekle çelişir.
 *
 * [K-010] "firma bağlamı yapısal olarak oturuma bağlıdır" der. Bu ilkenin
 * istemcideki karşılığı: bağlamı SUNUCUYA SORARIZ, hatırlamayız.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { ApiError, istek } from '../lib/api.ts';

export interface Firm {
  readonly id: number;
  readonly code: string;
}

/** `/auth/me` cevabı — alan adları sunucudakiyle birebir. */
export interface Session {
  readonly userId: number;
  readonly sessionFirmId: number;
  readonly isOperator: boolean;
  /** Operatörün seçtiği firma; seçim yoksa `null`. */
  readonly activeFirmId: number | null;
  /**
   * RLS'e giden ETKİN kapsam. Operatör seçim yapmadıysa `null` — tüm
   * firmaları okur ama hiçbirine yazamaz [K-010].
   */
  readonly effectiveFirmId: number | null;
  readonly permissions: readonly string[];
  readonly visibleFirms: readonly Firm[];
}

export const SESSION_KEY = ['auth', 'me'] as const;

/**
 * Oturumu getirir.
 *
 * 401 bir HATA DEĞİL, bir CEVAPTIR: "giriş yapılmamış". Bu yüzden `null`'a
 * çevrilir — aksi hâlde her açılışta hata ekranı görünürdü. Diğer hatalar
 * (ağ, 500) hata olarak kalır; kullanıcıya "giriş yapın" demek yanlış olurdu.
 */
export function useSession(): UseQueryResult<Session | null> {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await istek<Session>('/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.oturumsuz) return null;
        throw error;
      }
    },
    // Oturum durumu her zaman taze olmalı: yetki değişimi gecikmeyle yansımasın.
    staleTime: 0,
    retry: false,
  });
}

/**
 * Oturumu YENİDEN GETİRİR — giriş, çıkış ve firma değişiminden sonra.
 *
 * ─── `invalidateQueries` TEK BAŞINA YETMEZ (testte ölçüldü) ──────────────────
 * O yalnız AKTİF gözlemcileri tazeler. Giriş anında `/auth/me`'yi dinleyen
 * hiçbir bileşen yoktur (korumalı kabuk henüz takılı değil), dolayısıyla
 * geçersiz kılma hiçbir istek üretmez.
 *
 * `fetchQuery` ile açıkça getiriyoruz. Kazancı sadece testin geçmesi değil:
 * yönlendirme yapıldığında oturum ZATEN elde olur, kullanıcı bir anlık
 * yükleniyor ekranı görmez.
 */
export function useSessionRefresh(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    qc.removeQueries({ queryKey: SESSION_KEY });
    await qc.fetchQuery({
      queryKey: SESSION_KEY,
      queryFn: async () => {
        try {
          return await istek<Session>('/auth/me');
        } catch (error) {
          if (error instanceof ApiError && error.oturumsuz) return null;
          throw error;
        }
      },
    });
  };
}

/**
 * Çıkış yapar ve oturumu temizler.
 *
 * Sunucu çağrısı BAŞARISIZ OLSA BİLE yerel oturum temizlenir. Gerekçe:
 * kullanıcı "çıkış" dediyse ekranda oturumlu kalmamalı — ağ koptuysa bile.
 * Sunucudaki oturum kaydı zaten kendi süresinde düşer.
 */
export function useLogout(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    try {
      await istek('/auth/logout', { method: 'POST' });
    } catch {
      /* yoksay — aşağıdaki temizlik her hâlükârda yapılır */
    }
    // Tüm önbellek silinir: bir sonraki kullanıcı öncekinin verisini GÖRMEMELİ.
    qc.clear();
  };
}

/**
 * İzin kontrolü — YALNIZ GÖRÜNÜRLÜK İÇİN.
 *
 * ⚠️ BU BİR GÜVENLİK KONTROLÜ DEĞİLDİR. Yetkisi olmayan kullanıcıya düğmeyi
 * göstermemek iyi bir deneyimdir; ama tarayıcıdaki hiçbir kontrol güvenlik
 * sağlamaz — kullanıcı isteği elle de gönderebilir.
 *
 * ASIL KAPI SUNUCUDADIR: `role_permissions` + RLS. İleride biri "istemci
 * zaten kontrol ediyor" diye sunucu kontrolünü atlamasın diye bu not burada
 * duruyor.
 */
export function izinVar(session: Session | null | undefined, izin: string): boolean {
  return session?.permissions.includes(izin) ?? false;
}
